import { useEffect, useMemo, useState, type ComponentType } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, CreditCard, FileText, Heart, Settings, Wallet } from "lucide-react";

import api from "../api/Client";
import { API_BASE } from "../config";
import styles from "../styles/UserPage.module.css";

type MeResponse = {
  data?: {
    user_id?: number;
    name?: string;
    email?: string;
    phone?: string;
    profile_photo?: string | null;
    wallet_balance?: number | null;
    active_bookings?: Array<unknown>;
  };
};

type MenuItem = {
  title: string;
  path: string;
  icon: ComponentType<{ size?: number }>;
  dataTour?: string;
};

const MENU_ITEMS: MenuItem[] = [
  { title: "Configuracion", path: "/settings", icon: Settings },
  { title: "Mis favoritos", path: "/favorites", icon: Heart, dataTour: "user-favoritos" },
  { title: "Documentos", path: "/documents", icon: FileText, dataTour: "user-documentos" },
  { title: "Metodos de pago", path: "/payment-methods", icon: CreditCard, dataTour: "user-mis-tarjetas" },
  { title: "Monedero virtual", path: "/ewallet", icon: Wallet },
  { title: "Anadir cupon", path: "/add-coupons", icon: Wallet },
];

export default function UserPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<MeResponse["data"] | null>(null);
  const [avatarFailed, setAvatarFailed] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function loadProfile() {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get<MeResponse>("/api/customers/me");
        if (!mounted) {
          return;
        }
        setProfile(response.data?.data ?? null);
      } catch (requestError: any) {
        if (!mounted) {
          return;
        }
        setError(requestError?.response?.data?.error || "No se pudo cargar tu perfil.");
        setProfile(null);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    void loadProfile();
    return () => {
      mounted = false;
    };
  }, []);

  const fullName = useMemo(() => {
    const name = String(profile?.name || "").trim();
    return name || "Cliente";
  }, [profile]);

  const initials = useMemo(() => {
    const parts = fullName.split(" ").filter(Boolean);
    if (parts.length === 0) {
      return "C";
    }
    if (parts.length === 1) {
      return parts[0].slice(0, 1).toUpperCase();
    }
    return `${parts[0].slice(0, 1)}${parts[parts.length - 1].slice(0, 1)}`.toUpperCase();
  }, [fullName]);

  const avatarSrc = useMemo(() => {
    const pic = String(profile?.profile_photo || "").trim();
    if (!pic || avatarFailed) {
      return "";
    }
    return `${API_BASE}/api/profile_pic/${pic}`;
  }, [profile, avatarFailed]);

  const walletBalance = Number(profile?.wallet_balance || 0);
  const activeBookings = Array.isArray(profile?.active_bookings) ? profile.active_bookings.length : 0;

  return (
    <main className={styles.page}>
      <section className={styles.heroCard}>
        <div className={styles.heroGradient} />
        <div className={styles.heroTop}>
          <div className={styles.avatar}>
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt="Foto de perfil"
                onError={() => setAvatarFailed(true)}
              />
            ) : (
              <span>{initials}</span>
            )}
          </div>

          <div className={styles.identity}>
            <h1>{fullName}</h1>
            <p>{profile?.email || "Sin email"}</p>
            <p>{profile?.phone || "Sin telefono"}</p>
          </div>
        </div>

        <div className={styles.heroStats}>
          <div className={styles.statBox}>
            <span className={styles.statLabel}>Saldo</span>
            <span className={styles.statValue}>{walletBalance.toFixed(2)} EUR</span>
          </div>
          <div className={styles.statBox}>
            <span className={styles.statLabel}>Reservas activas</span>
            <span className={styles.statValue}>{activeBookings}</span>
          </div>
        </div>

        <div className={styles.heroActions}>
          <button type="button" className={styles.btnSolid} onClick={() => navigate("/profile")}>
            Ver perfil
          </button>
          <button type="button" className={styles.btnGhost} onClick={() => navigate("/profile/edit")}>
            Editar perfil
          </button>
        </div>
      </section>

      <section className={styles.menuCard}>
        <h2>Mi espacio</h2>

        {loading && <p className={styles.stateText}>Cargando datos...</p>}
        {error && !loading && <p className={styles.errorText}>{error}</p>}

        {!loading && !error && (
          <div className={styles.menuList}>
            {MENU_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.title}
                  type="button"
                  className={styles.menuItem}
                  onClick={() => navigate(item.path)}
                  {...(item.dataTour ? { "data-tour": item.dataTour } : {})}
                >
                  <span className={styles.menuLeft}>
                    <span className={styles.menuIcon}>
                      <Icon size={17} />
                    </span>
                    <span>{item.title}</span>
                  </span>
                  <ChevronRight size={18} />
                </button>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}
